import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { TrendingUp, Loader2, Sparkles, AlertTriangle, Lightbulb, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, errMsg, runJob } from "../lib/api";

const likelihoodStyle = {
  High: "bg-emerald-500/15 text-emerald-500",
  Medium: "bg-amber-500/15 text-amber-500",
  Lower: "bg-rose-500/15 text-rose-400",
};

const CHART_COLORS = ["#6366F1", "#A855F7", "#3B82F6", "#06B6D4", "#EC4899", "#F59E0B", "#10B981", "#8B5CF6"];

function subtopicFacts(prediction, topic, subtopic) {
  const idx = prediction?.subtopic_index || {};
  const row = idx[topic]?.[subtopic];
  if (!row || row.no_major_essay || !row.last_essay_real_year) {
    return { noEssay: true };
  }
  return {
    noEssay: false,
    year: row.last_essay_real_year,
    seriesSince: row.series_since_last_essay,
  };
}

function seriesLabel(n) {
  if (n === 0) return "asked in latest series";
  if (n === 1) return "1 real series ago";
  return `${n} real series ago`;
}

function reviewEssayNote(n) {
  if (!n || n <= 0) return null;
  return n === 1
    ? "1 essay awaiting reconciliation — may affect this row once reconciled"
    : `${n} essays awaiting reconciliation — may affect this row once reconciled`;
}

export default function Predictions() {
  const [options, setOptions] = useState([]);
  const [sel, setSel] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genPaper, setGenPaper] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setOptions(r.data.options);
      setSel(r.data.options[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sel) return;
    setPrediction(null);
    Promise.all([
      api.get("/predictions", { params: { exam_board: sel.exam_board, subject: sel.subject } }),
      api.get("/predictions/overdue", { params: { exam_board: sel.exam_board, subject: sel.subject, limit: 10 } }).catch(() => null),
    ]).then(([pRes, oRes]) => {
      const p = pRes.data[0] || null;
      if (p && oRes?.data) {
        // Hydrate DB-sourced fields — older stored predictions predate this refactor
        p.overdue_watchlist = p.overdue_watchlist?.length ? p.overdue_watchlist : oRes.data.overdue || [];
        p.subtopic_index = p.subtopic_index && Object.keys(p.subtopic_index).length ? p.subtopic_index : oRes.data.subtopic_index || {};
        p.evidence_label = p.evidence_label || oRes.data.evidence_label;
      }
      setPrediction(p);
    }).catch(() => {});
    api.get("/analysis/stats", { params: { exam_board: sel.exam_board, subject: sel.subject } })
      .then((r) => setTrend(r.data.stats)).catch(() => setTrend(null));
  }, [sel]);

  const generate = async () => {
    setLoading(true);
    setPrediction(null);
    try {
      await runJob(() => api.post("/predictions/generate", { exam_board: sel.exam_board, subject: sel.subject, qualification: sel.qualification }));
      const [{ data }, oRes] = await Promise.all([
        api.get("/predictions", { params: { exam_board: sel.exam_board, subject: sel.subject } }),
        api.get("/predictions/overdue", { params: { exam_board: sel.exam_board, subject: sel.subject, limit: 10 } }).catch(() => null),
      ]);
      const p = data[0] || null;
      if (p && oRes?.data) {
        p.overdue_watchlist = p.overdue_watchlist?.length ? p.overdue_watchlist : oRes.data.overdue || [];
        p.subtopic_index = p.subtopic_index && Object.keys(p.subtopic_index).length ? p.subtopic_index : oRes.data.subtopic_index || {};
        p.evidence_label = p.evidence_label || oRes.data.evidence_label;
      }
      setPrediction(p);
      toast.success("Predictions generated!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const generatePredictedPaper = async () => {
    setGenPaper(true);
    try {
      const highTopics = (prediction?.topic_predictions || []).filter((t) => t.likelihood !== "Lower").map((t) => t.topic).slice(0, 4);
      const job = await runJob(() => api.post("/papers/generate", {
        exam_board: sel.exam_board, subject: sel.subject, qualification: sel.qualification,
        paper_number: sel.papers[0], topics: highTopics, difficulty: "Standard",
        time_allowed: 120, total_marks: 96, num_questions: 8, year_start: sel.year_min, year_end: sel.year_max, predicted: true,
      }));
      toast.success("Predicted paper generated!");
      navigate(`/papers/${job.result_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenPaper(false);
    }
  };

  const topicKeys = trend ? trend.topic_frequency.map((t) => t.topic) : [];
  const overdue = prediction?.overdue_watchlist || [];
  const evidenceLabel = prediction?.evidence_label || "Based on 7 real exam series: 2017-2019, 2022-2025";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="predictions-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Prediction Engine</p>
      <h1 className="text-4xl font-bold tracking-tighter">AI Predictions</h1>
      <p className="mt-1 text-xs text-muted-foreground" data-testid="prediction-evidence-caption">{evidenceLabel}</p>
      <p className="mt-3 text-muted-foreground max-w-2xl">Statistical models over historical papers, mark schemes and specifications — explained by AI.</p>

      <div className="mt-6 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 flex gap-2 items-center" data-testid="prediction-disclaimer">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <p className="text-xs text-muted-foreground truncate">Predictions are statistical estimates, <span className="font-semibold text-foreground">not guarantees</span> — revise the whole spec, but prioritise smartly.</p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Select value={sel ? `${sel.exam_board}|${sel.subject}` : ""} onValueChange={(v) => { const [b, s] = v.split("|"); setSel(options.find((o) => o.exam_board === b && o.subject === s)); }}>
          <SelectTrigger className="w-72" data-testid="prediction-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={`${o.exam_board}|${o.subject}`} value={`${o.exam_board}|${o.subject}`}>{o.exam_board} {o.qualification} {o.subject}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="generate-predictions-btn" onClick={generate} disabled={loading || !sel} className="rounded-full glow-btn">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analysing…</> : <><TrendingUp className="h-4 w-4 mr-2" />{prediction ? "Refresh Predictions" : "Generate Predictions"}</>}
        </Button>
        {prediction && (
          <Button variant="outline" data-testid="generate-predicted-paper-btn" onClick={generatePredictedPaper} disabled={genPaper} className="rounded-full">
            {genPaper ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Building paper…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Predicted Paper</>}
          </Button>
        )}
      </div>

      {loading && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid="prediction-loading-hint">
          Running a deep analysis across all 17 topics and their subtopics — this can take up to 5–8 minutes. Feel free to browse other pages; the analysis continues in the background.
        </p>
      )}

      {!prediction && !loading && (
        <div className="mt-10 rounded-3xl border border-dashed border-border p-16 text-center">
          <p className="text-muted-foreground text-sm">No predictions yet for this subject. Hit <span className="text-primary font-medium">Generate Predictions</span> to run the analysis.</p>
        </div>
      )}

      {prediction && (
        <>
          {overdue.length > 0 && (
            <div className="mt-8" data-testid="overdue-strip">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-semibold tracking-tight flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />Top overdue</h2>
                <p className="text-xs text-muted-foreground">Subtopics longest overdue an 8+ mark essay, ranked by real series since the last major essay</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {overdue.map((o, i) => (
                  <Link
                    key={`${o.topic}|${o.subtopic}`}
                    to={`/topics/${encodeURIComponent(o.topic)}`}
                    data-testid={`overdue-tile-${i}`}
                    className="group rounded-2xl border border-border bg-card p-4 hover:border-primary/50 hover:bg-primary/[0.04] transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground truncate">{o.topic}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`overdue-gap-${i}`}>{o.series_since_last_essay}× overdue</Badge>
                    </div>
                    <p className="mt-1.5 text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">{o.subtopic}</p>
                    <p className="mt-2 text-xs text-muted-foreground" data-testid={`overdue-last-essay-${i}`}>
                      {o.last_essay_real_year
                        ? <><span className="font-medium text-foreground">Last major essay (8+ marks): {o.last_essay_real_year}</span> · {seriesLabel(o.series_since_last_essay)}</>
                        : <span className="italic">No major essay in analysed papers (2017-2019, 2022-2025)</span>}
                    </p>
                    {o.hidden_review_essays > 0 && (
                      <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-500" data-testid={`overdue-hidden-badge-${i}`}>
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        <span>{reviewEssayNote(o.hidden_review_essays)}</span>
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="prediction-list">
            {prediction.topic_predictions.map((t) => {
              const score = (prediction.topic_scores || []).find((x) => x.topic === t.topic) || {};
              const ep = score.essay_probability ?? t.essay_probability ?? 0;
              return (
              <div key={t.topic} className="card-lift rounded-3xl border border-border bg-card p-6" data-testid={`prediction-card-${t.topic.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between gap-3">
                  <Link to={`/topics/${encodeURIComponent(t.topic)}`} data-testid={`prediction-topic-link-${t.topic.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-semibold tracking-tight hover:text-primary transition-colors">{t.topic}</Link>
                  <Badge className={`${likelihoodStyle[t.likelihood] || likelihoodStyle.Medium} hover:${likelihoodStyle[t.likelihood]} shrink-0`}>{t.likelihood} priority</Badge>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Chance of an 8/16-mark question</span><span className="font-semibold">{ep >= 0.99 ? "99%+" : `${Math.round(ep * 100)}%`}</span></div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500" style={{ width: `${Math.round(ep * 100)}%` }} />
                  </div>
                </div>
                {t.likely_essay_focus && (
                  <div className="mt-4 rounded-2xl border border-primary/25 bg-primary/5 p-4" data-testid={`essay-focus-${t.topic.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary mb-1.5 flex items-center gap-1.5"><Sparkles className="h-3 w-3" />Most likely big question</p>
                    <p className="text-sm font-medium leading-snug">&ldquo;{t.likely_essay_focus}&rdquo;</p>
                  </div>
                )}
                {(t.likely_subtopics || []).length > 0 && (
                  <div className="mt-4 space-y-2.5" data-testid={`likely-subtopics-${t.topic.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Watch these subtopics</p>
                    {t.likely_subtopics.slice(0, 3).map((s, i) => {
                      const facts = subtopicFacts(prediction, t.topic, s.subtopic);
                      return (
                        <div key={i} className="rounded-xl bg-secondary p-3" data-testid={`watchlist-row-${t.topic.toLowerCase().replace(/\s+/g, "-")}-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold">{s.subtopic}</p>
                            {s.expected_style && <Badge variant="outline" className="text-[10px] shrink-0">{s.expected_style}</Badge>}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-[11px]" data-testid={`watchlist-facts-${t.topic.toLowerCase().replace(/\s+/g, "-")}-${i}`}>
                            {facts.noEssay ? (
                              <span className="text-muted-foreground italic">No major essay in analysed papers</span>
                            ) : (
                              <>
                                <span className="font-semibold text-foreground">{facts.year}</span>
                                <span className="text-muted-foreground">— {seriesLabel(facts.seriesSince)}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{s.why}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(t.recurring_patterns || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-1.5">What keeps showing up</p>
                    <ul className="space-y-1">
                      {t.recurring_patterns.slice(0, 3).map((p, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-primary">•</span>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );})}
          </div>

          <div className="mt-8 rounded-3xl border border-border bg-card p-6 md:p-8">
            <h3 className="font-semibold tracking-tight mb-2">Analysis Summary</h3>
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="prediction-summary">{prediction.summary}</p>
          </div>

          {prediction.revision_tips?.length > 0 && (
            <div className="mt-6 rounded-3xl border border-border bg-card p-6 md:p-8">
              <h3 className="font-semibold tracking-tight mb-4 flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" />Strategic Revision Tips</h3>
              <ul className="space-y-2">
                {prediction.revision_tips.map((tip, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2.5"><span className="text-primary font-semibold">{i + 1}.</span>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {trend && (
        <div className="mt-6 rounded-3xl border border-border bg-card p-6 md:p-8">
          <h3 className="font-semibold tracking-tight mb-1">Topic Trend Over Time</h3>
          <p className="text-xs text-muted-foreground mb-5">Question appearances per year by topic</p>
          <div className="h-72" data-testid="topic-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.topic_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                {topicKeys.map((t, i) => <Line key={t} type="monotone" dataKey={t} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
